import * as React from 'react';
import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { UnifiedEvent } from '../types';

const HOLIDAY_COLOR = '#16a34a';
const EVENT_COLOR = '#3b82f6';

const TYPE_LABELS: Record<string, string> = {
  public_holiday: 'Feiertag',
  world_day: 'Welttag',
  city_event: 'City Event',
  local_event: 'Lokales Event',
  custom_event: 'Eigenes Event',
};

function formatDateDE(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function formatRange(start: string, end: string | null): string {
  if (!end || end === start) return formatDateDE(start);
  return `${formatDateDE(start)} – ${formatDateDE(end)}`;
}

interface Props {
  events: UnifiedEvent[];
  children: React.ReactNode;
}

export default function ShiftEventIndicator({ events, children }: Props): React.ReactElement {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const handleEnter = () => {
    if (events.length === 0) return;
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
    setShow(true);
  };

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        style={{ cursor: events.length > 0 ? 'default' : undefined }}
      >
        {children}
      </span>

      {show && events.length > 0 && createPortal(
        <div
          style={{
            position: 'absolute',
            top: pos.top,
            left: pos.left,
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: '10px 14px',
            minWidth: 220,
            maxWidth: 320,
          }}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          {events.map((ev, i) => {
            const color = ev.isHoliday ? HOLIDAY_COLOR : EVENT_COLOR;
            return (
              <div
                key={`${ev.type}-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '4px 0',
                  borderBottom: i < events.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}
              >
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 5 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0E1333' }}>{ev.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span>{TYPE_LABELS[ev.type]}</span>
                    {ev.endDate && ev.endDate !== ev.date && (
                      <span>{formatRange(ev.date, ev.endDate)}</span>
                    )}
                    {ev.location && <span>{ev.location}</span>}
                  </div>
                  {ev.visitors && ev.visitors > 0 && (
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>
                      ~{ev.visitors.toLocaleString('de-DE')} Besucher erwartet
                    </div>
                  )}
                  {ev.keywords && ev.keywords.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                      {ev.keywords.slice(0, 3).map((kw, ki) => (
                        <span
                          key={ki}
                          style={{
                            fontSize: 9,
                            padding: '1px 5px',
                            borderRadius: 6,
                            background: ev.isHoliday ? '#dcfce7' : '#dbeafe',
                            color: ev.isHoliday ? '#166534' : '#1e40af',
                          }}
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
